import React, { useState } from 'react';
import MediumButton from '../ui/MediumButton';
import {
  CloseIcon,
  RotateReverseIcon
} from '../../utils/icons';

import { LargeSpinner } from '../../utils/loaders';

// Arrow icons for navigation
const LeftArrowIcon = ({ isDisabled = false }) => (
  <svg width="16" height="16" viewBox="0 0 17 16" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path 
      d="M10.5 10.2997C10.5 11.1906 9.42286 11.6367 8.79289 11.0068L6.49306 8.70693C6.10254 8.3164 6.10254 7.68324 6.49306 7.29271L8.79289 4.99288C9.42286 4.36292 10.5 4.80908 10.5 5.69999V10.2997Z" 
      fill={isDisabled ? "#726E6C" : "#FAF7F5"}
    />
  </svg>
);

const RightArrowIcon = ({ isDisabled = false }) => (
  <svg width="16" height="16" viewBox="0 0 17 16" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path 
      d="M6.5 5.70035C6.5 4.80944 7.57714 4.36327 8.20711 4.99324L10.5069 7.29307C10.8975 7.6836 10.8975 8.31676 10.5069 8.70728L8.20711 11.0071C7.57714 11.6371 6.5 11.1909 6.5 10.3V5.70035Z" 
      fill={isDisabled ? "#726E6C" : "#FAF7F5"}
    />
  </svg>
);

const LyricsSelectionModal = ({ lyricsOptions, onSelect, onClose, onRegenerate, isGenerating, originalSelection, wasFromEmptyLyrics = false, originalLyrics = "", currentSelection = null, hideBeforePanel = false }) => {
    const [currentVersionIndex, setCurrentVersionIndex] = useState(0);

    const canGoPrevious = currentVersionIndex > 0;
    const canGoNext = currentVersionIndex < lyricsOptions.length - 1;

    const handlePrevious = () => {
        if (canGoPrevious) {
            setCurrentVersionIndex(currentVersionIndex - 1);
        }
    };

    const handleNext = () => {
        if (canGoNext) {
            setCurrentVersionIndex(currentVersionIndex + 1);
        }
    };

    const currentOption = lyricsOptions[currentVersionIndex];

    const renderOriginalWithHighlight = () => {
        // If we have currentSelection (text selection enhancement), show full lyrics with highlight
        if (currentSelection && originalLyrics) {
            const { start, end } = currentSelection;
            const before = originalLyrics.substring(0, start);
            const selected = originalLyrics.substring(start, end);
            const after = originalLyrics.substring(end);

            return (
                <>
                    {before}
                    <span className="bg-[#FD429C] text-[#FAF7F5] px-1 rounded">
                        {selected}
                    </span>
                    {after}
                </>
            );
        }
        
        // Otherwise, show the full original lyrics
        return originalLyrics || "No original lyrics";
    };

    return (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[60]">
            <div className="w-[700px] flex flex-col rounded-3xl bg-[#252020] overflow-hidden pb-6">
                <div className="flex justify-between items-center p-6">
                    <MediumButton variant="secondary" onClick={onRegenerate} leftIcon={<RotateReverseIcon />} />
                    <h2 className="text-lg font-semibold text-[#faf7f5]">Select Lyrics</h2>
                    <MediumButton variant="secondary" onClick={onClose} leftIcon={<CloseIcon />} />
                </div>
                
                {isGenerating ? (
                    <div className="w-full h-96 flex flex-col items-center justify-center text-[#faf7f5] gap-4 px-6">
                        <LargeSpinner />
                        Generating...
                    </div>
                ) : hideBeforePanel ? (
                    /* Side-by-side layout for prompt generation */
                    <div className="flex gap-6 px-6">
                        {lyricsOptions.map((option, index) => (
                            <div key={index} className="flex-1 flex flex-col gap-4 p-4 rounded-lg bg-[#363030]">
                                <h3 className="text-lg font-medium text-[#faf7f5]">
                                    Version {index + 1}
                                </h3>
                                <div className="text-base text-[#d1cbc7] whitespace-pre-wrap overflow-y-auto max-h-[400px] text-left">
                                    {option?.lyrics}
                                </div>
                                <MediumButton 
                                    onClick={() => onSelect(option.lyrics)} 
                                    variant="primary" 
                                    text={`Select version ${index + 1}`}
                                />
                            </div>
                        ))}
                    </div>
                ) : (
                    /* Original before/after layout for enhancement */
                    <div className="flex gap-6 px-6">
                        {/* Original Lyrics Panel */}
                        <div className="flex-1 flex flex-col gap-4 p-4 rounded-lg bg-[#363030]">
                            <h3 className="text-lg font-medium text-[#faf7f5]">Before</h3>
                            <div className="text-base text-[#d1cbc7] whitespace-pre-wrap overflow-y-auto max-h-[400px] text-left">
                                {renderOriginalWithHighlight()}
                            </div>
                        </div>

                        {/* Version Panel */}
                        <div className="flex-1 flex flex-col gap-4 p-4 rounded-lg bg-[#363030]">
                            <h3 className="text-lg font-medium text-[#faf7f5]">
                                After
                            </h3>
                            <div className="text-base text-[#d1cbc7] whitespace-pre-wrap overflow-y-auto max-h-[400px] text-left">
                                {currentOption?.lyrics}
                            </div>
                            
                            {/* Navigation Controls */}
                            <div className="flex justify-center items-center gap-2.5">
                                <button
                                    onClick={handlePrevious}
                                    disabled={!canGoPrevious}
                                    className={`w-10 h-10 flex justify-center items-center rounded-full border border-white/10 transition-colors ${
                                        canGoPrevious ? 'hover:bg-white/5' : 'opacity-50 cursor-not-allowed'
                                    }`}
                                >
                                    <LeftArrowIcon isDisabled={!canGoPrevious} />
                                </button>
                                
                                <MediumButton 
                                    onClick={() => onSelect(currentOption.lyrics)} 
                                    variant="primary" 
                                    text={currentOption?.title && wasFromEmptyLyrics ? currentOption.title : `Select version ${currentVersionIndex + 1}`}
                                />
                                
                                <button
                                    onClick={handleNext}
                                    disabled={!canGoNext}
                                    className={`w-10 h-10 flex justify-center items-center rounded-full border border-white/10 transition-colors ${
                                        canGoNext ? 'hover:bg-white/5' : 'opacity-50 cursor-not-allowed'
                                    }`}
                                >
                                    <RightArrowIcon isDisabled={!canGoNext} />
                                </button>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default LyricsSelectionModal;